import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';

// Ícones
import AltRouteIcon from '@mui/icons-material/AltRoute';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import SensorsIcon from '@mui/icons-material/Sensors';
import LoginIcon from '@mui/icons-material/Login';

export default function LandingPage() {
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const [isScrolled, setIsScrolled] = useState(false);

  const isLoggedIn = !!(auth && auth.profile && auth.profile.uid);
  const userRole = auth?.profile?.usertype;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCtaClick = () => {
    if (isLoggedIn) {
      const isSuperAdmin = Boolean(
        auth?.profile?.is_superadmin ||
        auth?.profile?.role === 'superadmin' ||
        auth?.profile?.usertype === 'superadmin'
      );
      if (isSuperAdmin) {
        navigate('/tenants');
      } else if (userRole === 'driver') {
        navigate('/bookings');
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <Box sx={{ bgcolor: '#0f172a', color: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
      <Helmet>
        <title>Alpha Giro - Controle de Frota & Rotas</title>
        <meta
          name="description"
          content="Plataforma moderna de gestão de frota, telemetria em tempo real e controle de rotas corporativas."
        />
      </Helmet>

      {/* 1. Navbar Moderna & Minimalista */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          transition: 'all 0.25s ease',
          bgcolor: isScrolled ? 'rgba(15, 23, 42, 0.92)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(12px)' : 'none',
          borderBottom: isScrolled ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
          py: 2
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Logo Brand */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <Avatar
                sx={{
                  bgcolor: '#2563eb',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)'
                }}
              >
                <AltRouteIcon sx={{ color: '#ffffff', fontSize: 22 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '18px', lineHeight: 1.1, letterSpacing: '-0.4px', color: '#ffffff' }}>
                  Alpha Giro
                </Typography>
                <Typography variant="caption" sx={{ color: '#38bdf8', fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                  Controle de Frota
                </Typography>
              </Box>
            </Box>

            {/* Menu Links (Desktop) */}
            <Stack direction="row" spacing={3.5} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              <Typography
                component="a"
                href="#recursos"
                sx={{ color: '#94a3b8', textDecoration: 'none', fontSize: '14px', fontWeight: 600, transition: 'color 0.15s', '&:hover': { color: '#ffffff' } }}
              >
                Recursos
              </Typography>
              <Typography
                component="a"
                href="#como-funciona"
                sx={{ color: '#94a3b8', textDecoration: 'none', fontSize: '14px', fontWeight: 600, transition: 'color 0.15s', '&:hover': { color: '#ffffff' } }}
              >
                Como Funciona
              </Typography>
              <Typography
                component="a"
                href="#app-motorista"
                sx={{ color: '#94a3b8', textDecoration: 'none', fontSize: '14px', fontWeight: 600, transition: 'color 0.15s', '&:hover': { color: '#ffffff' } }}
              >
                App do Condutor
              </Typography>
            </Stack>

            {/* Ação de Acesso */}
            <Button
              variant="contained"
              onClick={handleCtaClick}
              endIcon={isLoggedIn ? <ArrowForwardIcon sx={{ fontSize: 16 }} /> : <LoginIcon sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#2563eb',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '13.5px',
                borderRadius: '10px',
                px: 2.2,
                py: 0.9,
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                '&:hover': { bgcolor: '#1d4ed8' }
              }}
            >
              {isLoggedIn ? 'Ir para o Painel' : 'Acessar Sistema'}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* 2. Hero Section */}
      <Box
        sx={{
          pt: { xs: 16, md: 22 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
          background: 'radial-gradient(circle at 50% 10%, rgba(37, 99, 235, 0.18) 0%, transparent 60%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 840, mx: 'auto', mb: { xs: 6, md: 8 } }}>
            {/* Pill Badge */}
            <Chip
              icon={<SensorsIcon sx={{ fontSize: '14px !important', color: '#38bdf8 !important' }} />}
              label="TELEMETRIA & GESTÃO DE FROTAS CORPORATIVAS"
              size="small"
              sx={{
                bgcolor: 'rgba(56, 189, 248, 0.1)',
                color: '#38bdf8',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                fontWeight: 800,
                fontSize: '11px',
                letterSpacing: '0.5px',
                mb: 3,
                px: 1,
                py: 0.5
              }}
            />

            {/* Headline Principal */}
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '32px', sm: '46px', md: '56px' },
                lineHeight: 1.15,
                letterSpacing: '-1.5px',
                color: '#ffffff',
                mb: 2.5
              }}
            >
              Controle total da sua frota e rotas em{' '}
              <span style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #38bdf8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                tempo real
              </span>
            </Typography>

            {/* Subtítulo Descritivo */}
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                fontSize: { xs: '16px', md: '19px' },
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: 680,
                mx: 'auto',
                mb: 4.5
              }}
            >
              Monitore veículos em campo, despache ordens de serviço com rotas inteligentes e obtenha indicadores precisos de desempenho para a sua operação.
            </Typography>

            {/* CTAs */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleCtaClick}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: '#2563eb',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '15px',
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 3.5,
                  py: 1.4,
                  boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                {isLoggedIn ? 'Abrir Torre de Controle' : 'Entrar na Plataforma'}
              </Button>
              <Button
                component="a"
                href="#recursos"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: '#334155',
                  color: '#e2e8f0',
                  fontWeight: 600,
                  fontSize: '15px',
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 3,
                  py: 1.4,
                  bgcolor: 'rgba(30, 41, 59, 0.4)',
                  '&:hover': { borderColor: '#64748b', bgcolor: 'rgba(30, 41, 59, 0.8)' }
                }}
              >
                Conhecer Recursos
              </Button>
            </Stack>

            {/* Destaques Rápidos */}
            <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" sx={{ gap: 1.5, color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                <span>Multi-Empresa (Multi-Tenant)</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                <span>Rastreamento GPS em Segundo Plano</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                <span>Despacho Direto via Web</span>
              </Box>
            </Stack>
          </Box>

          {/* 3. Preview Interativo da Torre de Controle */}
          <Box
            sx={{
              bgcolor: '#1e293b',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(37, 99, 235, 0.15)',
              overflow: 'hidden',
              maxWidth: 960,
              mx: 'auto'
            }}
          >
            {/* Header do Card Preview */}
            <Box
              sx={{
                px: 3,
                py: 2,
                bgcolor: '#0f172a',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Typography sx={{ ml: 1, fontSize: '13px', fontWeight: 700, color: '#94a3b8' }}>
                  Torre de Controle • Telemetria em Tempo Real
                </Typography>
              </Box>
              <Chip
                icon={<SensorsIcon sx={{ fontSize: '12px !important', color: '#16a34a !important' }} />}
                label="LIVE 60fps"
                size="small"
                sx={{ height: 22, fontSize: '10.5px', fontWeight: 800, bgcolor: '#dcfce7', color: '#15803d' }}
              />
            </Box>

            {/* Conteúdo Simulado do Dashboard */}
            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
              <Grid container spacing={2.5}>
                {/* Mini KPI 1 */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ bgcolor: 'rgba(15, 23, 42, 0.6)', p: 2.5, borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Veículos em Operação
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', mt: 0.5 }}>
                      100%
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 700 }}>
                      ● Todos em rota ou disponíveis
                    </Typography>
                  </Box>
                </Grid>

                {/* Mini KPI 2 */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ bgcolor: 'rgba(15, 23, 42, 0.6)', p: 2.5, borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Pontualidade das Entregas
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#38bdf8', mt: 0.5 }}>
                      99.4%
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                      Baseado nas últimas 50 rotas
                    </Typography>
                  </Box>
                </Grid>

                {/* Mini KPI 3 */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ bgcolor: 'rgba(15, 23, 42, 0.6)', p: 2.5, borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Multi-Tenant Ativo
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#a855f7', mt: 0.5 }}>
                      Isolado
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                      Segregação por empresa cliente
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 4. Seção de Recursos Principais */}
      <Box id="recursos" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#0f172a' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: 700, mx: 'auto' }}>
            <Typography variant="overline" sx={{ color: '#38bdf8', fontWeight: 800, letterSpacing: '1.2px' }}>
              FUNCIONALIDADES
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '28px', md: '38px' }, color: '#ffffff', mt: 1, letterSpacing: '-0.5px' }}>
              Tudo o que sua operação precisa para rodar sem atritos
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', mt: 2, fontSize: '16px' }}>
              Deixe para trás planilhas manuais e ferramentas dispersas. Tenha controle centralizado de frotas e equipes.
            </Typography>
          </Box>

          <Grid container spacing={3.5}>
            {/* Card 1: Torre de Controle */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#1e293b',
                  color: '#ffffff',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  height: '100%',
                  transition: 'transform 0.2s, border-color 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', borderColor: '#3b82f6' }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(37, 99, 235, 0.15)', color: '#38bdf8', width: 48, height: 48, borderRadius: '12px', mb: 2.5 }}>
                    <DashboardIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', mb: 1 }}>
                    Torre de Controle
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    Mapa interativo com localização GPS dos veículos, status online/offline e velocidade em tempo real.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 2: Despacho e Roteirização */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#1e293b',
                  color: '#ffffff',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  height: '100%',
                  transition: 'transform 0.2s, border-color 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', borderColor: '#3b82f6' }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', width: 48, height: 48, borderRadius: '12px', mb: 2.5 }}>
                    <AltRouteIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', mb: 1 }}>
                    Despacho de Rotas
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    Distribua ordens de serviço com paradas intermediárias, horários agendados e notificações diretas para os motoristas.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 3: Gestão de Veículos */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#1e293b',
                  color: '#ffffff',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  height: '100%',
                  transition: 'transform 0.2s, border-color 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', borderColor: '#3b82f6' }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(234, 179, 8, 0.15)', color: '#eab308', width: 48, height: 48, borderRadius: '12px', mb: 2.5 }}>
                    <DirectionsCarIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', mb: 1 }}>
                    Frota & Placas
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    Cadastro de veículos com padrão Mercosul, hodômetro, vinculação com condutores e prontidão operacional.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 4: Multi-Tenancy */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: '#1e293b',
                  color: '#ffffff',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  height: '100%',
                  transition: 'transform 0.2s, border-color 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', borderColor: '#3b82f6' }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', width: 48, height: 48, borderRadius: '12px', mb: 2.5 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', mb: 1 }}>
                    Multi-Empresa
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    Isolamento completo de dados por cliente ou filial através de Row Level Security (RLS) no Supabase.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 5. Como Funciona (3 Passos Simples) */}
      <Box id="como-funciona" sx={{ py: { xs: 10, md: 14 }, bgcolor: '#111827', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: '#38bdf8', fontWeight: 800, letterSpacing: '1.2px' }}>
              FLUXO OPERACIONAL
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '28px', md: '36px' }, color: '#ffffff', mt: 1 }}>
              Simples de implantar, potente para escalar
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', px: 2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '48px', color: 'rgba(56, 189, 248, 0.3)', lineHeight: 1 }}>
                  01
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 1.5, mb: 1, color: '#ffffff' }}>
                  Cadastre Veículos & Equipe
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Adicione os automóveis da sua frota e convide motoristas para o aplicativo móvel com poucos cliques.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', px: 2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '48px', color: 'rgba(56, 189, 248, 0.3)', lineHeight: 1 }}>
                  02
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 1.5, mb: 1, color: '#ffffff' }}>
                  Despache as Rotas
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Crie itinerários de serviço, defina o veículo e o motorista responsável. A rota é enviada instantaneamente ao celular do condutor.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', px: 2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '48px', color: 'rgba(56, 189, 248, 0.3)', lineHeight: 1 }}>
                  03
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 1.5, mb: 1, color: '#ffffff' }}>
                  Acompanhe na Torre ao Vivo
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Visualize a localização em tempo real no mapa com registro de início, paradas e conclusão de cada trajeto.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 6. App do Condutor */}
      <Box id="app-motorista" sx={{ py: { xs: 10, md: 14 }, bgcolor: '#0f172a' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              bgcolor: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              p: { xs: 3.5, md: 6 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 4
            }}
          >
            <Box sx={{ maxWidth: 540 }}>
              <Chip
                icon={<SmartphoneIcon sx={{ fontSize: '14px !important', color: '#38bdf8 !important' }} />}
                label="APLICATIVO MÓVEL"
                size="small"
                sx={{ bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', fontWeight: 800, mb: 2 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '26px', md: '34px' }, color: '#ffffff', mb: 2 }}>
                App dedicado para condutores em trânsito
              </Typography>
              <Typography variant="body1" sx={{ color: '#94a3b8', lineHeight: 1.7, mb: 3 }}>
                O condutor recebe rotas atribuídas, confere detalhes do itinerário e transmite a telemetria GPS em segundo plano sem complicações operacionais.
              </Typography>
              <Button
                variant="contained"
                onClick={handleCtaClick}
                sx={{
                  bgcolor: '#2563eb',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '10px',
                  px: 3,
                  py: 1,
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                Acessar Operação
              </Button>
            </Box>

            <Box
              sx={{
                bgcolor: '#1e293b',
                p: 3,
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                minWidth: { xs: '100%', sm: 300 },
                textAlign: 'center'
              }}
            >
              <SmartphoneIcon sx={{ fontSize: 64, color: '#38bdf8', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, color: '#ffffff', fontSize: '16px' }}>
                Alpha Giro Driver
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                Compatível com iOS & Android
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 7. Footer Limpo e Profissional */}
      <Box component="footer" sx={{ py: 6, bgcolor: '#090d16', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Avatar sx={{ bgcolor: '#2563eb', width: 32, height: 32, borderRadius: '8px' }}>
                <AltRouteIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#ffffff' }}>
                Alpha Giro
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', ml: 1 }}>
                © {new Date().getFullYear()} Todos os direitos reservados.
              </Typography>
            </Box>

            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ gap: 1 }}>
              <Typography
                component={Link}
                to="/about-us"
                sx={{ color: '#64748b', textDecoration: 'none', fontSize: '13px', '&:hover': { color: '#ffffff' } }}
              >
                Sobre Nós
              </Typography>
              <Typography
                component={Link}
                to="/term-condition"
                sx={{ color: '#64748b', textDecoration: 'none', fontSize: '13px', '&:hover': { color: '#ffffff' } }}
              >
                Termos de Uso
              </Typography>
              <Typography
                component={Link}
                to="/privacy-policy"
                sx={{ color: '#64748b', textDecoration: 'none', fontSize: '13px', '&:hover': { color: '#ffffff' } }}
              >
                Privacidade
              </Typography>
              <Typography
                component={Link}
                to="/contact-us"
                sx={{ color: '#64748b', textDecoration: 'none', fontSize: '13px', '&:hover': { color: '#ffffff' } }}
              >
                Contato
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
